<template>
  <div>
    <div class="questionnaire-form elevation-2 pa-4">
      <h3 class="appMain--text text-center mb-4">
        {{ questionnaireForm.title }}
      </h3>

      <action-pre-screen-intro-video
        v-if="showMediaBefore"
        v-model="introVideoWatched"
        :state="state"
        :introVideoUrl="introVideoUrl"
      />

      <v-form v-if="showForm" ref="form" lazy-validation>
        <div
          class="mb-6"
          v-if="questionnaireForm.introText"
          v-html="questionnaireForm.introText"
        ></div>

        <template v-for="(fieldSchema, field, idx) in formFields">
          <h4 v-if="fieldSchema.group_title" :key="idx" class="mb-4 mt-6">
            {{ fieldSchema.group_title }}
          </h4>
          <field-block v-if="isFieldVisible(fieldSchema)" :key="field">
            <div slot="title" v-html="fieldSchema.title"></div>
            <p v-html="fieldSchema.help"></p>
            <form-field
              :schema="fieldSchema"
              :field="field"
              v-model="form[field]"
            />
          </field-block>
        </template>

        <v-btn class="mt-6" color="primary" @click="sendForm">Send Form</v-btn>
      </v-form>

      <div class="success-message" v-if="formSent">
        <h3>Form successfully sent!</h3>
        <v-btn class="mt-2" color="appMain" @click="editForm"> Edit </v-btn>
      </div>
    </div>

    <div v-if="showMediaAfter" class="elevation-2 pa-4 mt-4 light">
      <p class="mb-2">
        <strong>{{ mediaAfterTitle }}</strong>
      </p>
      <action-entertain-content :state="mediaAfter" />
    </div>
  </div>
</template>

<script>
import FormField from '@/components/formFields/FormField';
import ActionPreScreenIntroVideo from '@/components/rooms/actions/questionnaire/ActionPreScreenIntroVideo';
import ActionEntertainContent from '@/components/rooms/actions/entertain/ActionEntertainContent';

import { mapState, mapMutations } from 'vuex';
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import questionnaires from '@/components/rooms/data/questionnaries';

export default {
  name: 'ActionPreScreenContent',
  components: {
    FormField,
    ActionPreScreenIntroVideo,
    ActionEntertainContent,
  },
  data() {
    return {
      form: null,
      formSent: false,
      formEditing: false,
      introVideoWatched: true,
    };
  },
  props: {
    state: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapState('rooms', ['roomInfo', 'guestAnswers']),
    showForm() {
      return (
        !!this.form &&
        !this.formSent &&
        (this.introVideoWatched || !this.showMediaBefore)
      );
    },
    isValid() {
      return this.$refs.form.validate();
    },
    introVideoUrl() {
      return this.state?.introVideo?.value;
    },
    questionnaireKey() {
      const key = this.state?.questionnaire;
      return key?.value || key;
    },
    questionnaireForm() {
      if (!this.questionnaireKey) return '';
      return questionnaires[this.questionnaireKey];
    },
    formFields() {
      return this.questionnaireForm?.fields || {};
    },
    showMediaBefore() {
      return !this.formSent && !this.formEditing && this.introVideoUrl;
    },
    mediaAfter() {
      const { outroAudio, outroVideo } = this.state;
      if (!outroAudio && !outroVideo) return null;
      return {
        videoUrl: outroVideo?.value,
        audioUrl: outroAudio?.value,
      };
    },
    mediaAfterTitle() {
      let str = 'Watch/listen';
      if (!this.mediaAfter.videoUrl) {
        str = 'Listen';
      }
      if (!this.mediaAfter.audioUrl) {
        str = 'Watch';
      }
      return `${str} while you wait`;
    },
    showMediaAfter() {
      return this.formSent && this.state.showMedia;
    },
  },
  watch: {
    questionnaireForm(form, prevForm) {
      if (!form || isEqual(form, prevForm)) return;
      this.prepareForm();
    },
  },
  methods: {
    ...mapMutations('rooms', ['setGuestAnswers']),
    editForm() {
      this.formEditing = true;
      this.formSent = false;
    },
    isFieldVisible(fieldSchema) {
      const { depend_on, depend_value, office } = fieldSchema;
      if (office) {
        return (this.roomInfo?.on || '').startsWith(office);
      }
      return !depend_on || (depend_on && this.form[depend_on] === depend_value);
    },

    sendForm() {
      if (!this.isValid) return;

      this.formSent = true;
      this.formEditing = false;

      const answer = {
        title: this.questionnaireForm.label,
        key: this.questionnaireKey,
        answer: cloneDeep(this.form),
      };

      this.$emit('send', cloneDeep(answer));
      this.saveAnswerLocally(answer);
    },

    saveAnswerLocally(answer) {
      const { name, identity, room } = this.roomInfo;
      const record = {
        name,
        identity,
        room,
        answers: [answer],
        timestamp: new Date().getTime(),
      };

      let savedRecord = this.guestAnswers;
      (savedRecord?.answers || []).forEach((a) => {
        if (!record.answers.find(({ key }) => key === a.key)) {
          record.answers.push(a);
        }
      });

      this.setGuestAnswers(record);
    },

    generateForm() {
      let form = {};
      for (let key in this.formFields) {
        let field = this.formFields[key];
        form[key] = field.type === 'Checkboxes' ? [] : '';
      }

      this.form = form;

      const savedGuestAnswers = this.guestAnswers;
      const record = (savedGuestAnswers?.answers || []).find(({ key }) => {
        return key === this.questionnaireKey;
      });

      if (record) {
        this.form = cloneDeep(record.answer);
        this.formSent = true;
      }
    },
    prepareForm() {
      this.formSent = false;
      this.generateForm();
    },
  },
  mounted() {
    this.prepareForm();
  },
};
</script>

<style lang="less">
.questionnaire-form {
  background-color: #fff;
  border-radius: @base-radius;
}

.success-message {
  display: flex;
  text-align: center;
  justify-content: center;
  flex-direction: column;
  padding: 30px 20px 50px;

  .v-icon {
    font-size: 60px;
  }
}
</style>
